import { Table, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table/interface";

import { formatBytes } from "@/views/PortfolioConnections/portfolioConnectionsConfig";
import { FeedCompositionRow } from "@/views/PortfolioConnections/portfolioConnections.types";

interface FeedCompositionTableProps {
  rows: FeedCompositionRow[];
}

/** Tooltip body for a feed: format + inventory description. */
function FeedTooltip({ row }: { row: FeedCompositionRow }) {
  return (
    <div className="flex flex-col gap-1 max-w-[280px] text-xs">
      {row.format ? (
        <span>
          <span className="opacity-70">Format: </span>
          {row.format}
        </span>
      ) : null}
      {row.description ? <span>{row.description}</span> : null}
    </div>
  );
}

const columns: ColumnsType<FeedCompositionRow> = [
  {
    title: "Source System",
    dataIndex: "sourceSystem",
    sorter: (a, b) => a.sourceSystem.localeCompare(b.sourceSystem),
  },
  {
    title: "Study",
    dataIndex: "study",
    sorter: (a, b) => a.study.localeCompare(b.study),
  },
  {
    title: "Feed",
    dataIndex: "feed",
    sorter: (a, b) => a.feed.localeCompare(b.feed),
    render: (feed: string, row) =>
      row.format || row.description ? (
        <span className="inline-flex items-center gap-1">
          {feed}
          <Tooltip title={<FeedTooltip row={row} />}>
            <InfoCircleOutlined className="text-[#556C75] text-xs" />
          </Tooltip>
        </span>
      ) : (
        feed
      ),
  },
  {
    title: "Files",
    dataIndex: "files",
    align: "right",
    sorter: (a, b) => a.files - b.files,
    render: (files: number) => files.toLocaleString("en-US"),
  },
  {
    title: "Size",
    dataIndex: "bytes",
    align: "right",
    sorter: (a, b) => a.bytes - b.bytes,
    render: (bytes: number) => formatBytes(bytes),
  },
  {
    title: "Loaded To",
    dataIndex: "destination",
    sorter: (a, b) => a.destination.localeCompare(b.destination),
  },
];

export default function FeedCompositionTable({
  rows,
}: FeedCompositionTableProps) {
  const totalFiles = rows.reduce((sum, row) => sum + row.files, 0);
  const totalBytes = rows.reduce((sum, row) => sum + row.bytes, 0);

  return (
    <Table<FeedCompositionRow>
      dataSource={rows}
      columns={columns}
      rowKey="key"
      pagination={false}
      size="small"
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={3}>
            <span className="font-medium">Total</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="right">
            <span className="font-medium">
              {totalFiles.toLocaleString("en-US")}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            <span className="font-medium">{formatBytes(totalBytes)}</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} />
        </Table.Summary.Row>
      )}
    />
  );
}
